import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ERROR_CODES, ORDER_PAYMENT_STATUSES, OrderType, PaymentGateway } from '../../services/exports/Constants';
import PaymentManager from '../../services/api/PaymentManager';
import { StoreInterface } from '../../store/types';
import { useHandleCheckoutResult } from './useCheckout';
import paymentLog from '../../services/helpers/PaymentLogger';

export const SpreedlyRedirectResultHandler = () => {
  useHandleSpreedlyRedirectResult();

  return null;
};

/**
 * How long after the customer's return the confirm is asked again while it answers 402, in
 * milliseconds between attempts. Adds up to the order screen's own 180 s payment-status budget. Each
 * ask re-reads Spreedly (and, for PayPal (direct), PayPal), which the order screen's poll does not:
 * it only reads our database, which nothing updates until a callback or the 15-minute sweep.
 *
 * Changing it changes backend traffic too: every ask is a Spreedly read, and for PayPal (direct) a
 * re-sync with PayPal (throttled server-side to one per 10 s, FetchSpreedlyTransactionStateAction).
 */
export const OFFSITE_RETURN_RETRY_DELAYS_MS = [5_000, 10_000, 20_000, 40_000, 50_000, 55_000];

/**
 * How long after the return this handler owns the outcome: the whole retry budget plus a margin
 * for the last ask to answer. Within it the order screen leaves a failed payment to the handler,
 * which sends the customer back to pay; past it the screen's own poll reports the failure.
 */
export const OFFSITE_RETURN_BUDGET_MS = OFFSITE_RETURN_RETRY_DELAYS_MS.reduce((total, delay) => total + delay, 10_000);

/**
 * Settles a Spreedly offsite payment (iDEAL, Bancontact, …) the moment the customer is back from
 * their bank. Spreedly appends `transaction_token` to our return URL; `offsite=1` and the order
 * `uuid` were put there at checkout (withOffsiteReturnParams).
 *
 * 200 paid → success. 409 → the bank refused or the customer backed out: they are sent back to the
 * payment step to pay again. 402 → not final yet: asked again on OFFSITE_RETURN_RETRY_DELAYS_MS,
 * while the order screen polls too. Anything else (a 5xx, a network error) says nothing about the
 * payment — the bank may well have approved it — so the customer stays on the order screen, whose
 * poll and Pusher decide; sending them back to pay could charge them twice.
 */
export default function useHandleSpreedlyRedirectResult() {
  const { company } = useSelector((store: StoreInterface) => store.initialData);

  const urlParams = new URLSearchParams(window.location.search);
  const isOffsiteReturn =
    urlParams.get('payment_gateway') === PaymentGateway.Spreedly && urlParams.get('offsite') === '1';
  const transactionToken = urlParams.get('transaction_token');
  const orderUuid = urlParams.get('uuid');

  const { onPaymentSuccess, onPaymentError } = useHandleCheckoutResult(urlParams.get('order_type') as OrderType, {
    setLoading: () => null,
  });

  async function handleRedirectResult(isCancelled: () => boolean) {
    if (!isOffsiteReturn || !transactionToken || !orderUuid) {
      return;
    }

    paymentLog.info('spreedly', 'offsite_return_received', { company_id: company.id, order_uuid: orderUuid });

    // This handler lives at the router, so navigating away never unmounts it. A customer who
    // leaves the return page while the answer is still pending — back to the basket, paying again
    // by card — must not be sent anywhere by a late answer for the attempt they walked away from.
    const startPathname = window.location.pathname;
    const left = () => {
      if (isCancelled()) {
        return true;
      }

      if (window.location.pathname === startPathname) {
        return false;
      }

      paymentLog.info('spreedly', 'offsite_return_left', { company_id: company.id, order_uuid: orderUuid });

      return true;
    };

    for (let attempt = 0; attempt <= OFFSITE_RETURN_RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, OFFSITE_RETURN_RETRY_DELAYS_MS[attempt - 1]));
      }

      if (left()) {
        return;
      }

      const { success, response, status } = await PaymentManager.submitSpreedlyPaymentDetails(
        { company_id: company.id, transaction_token: transactionToken, order_uuid: orderUuid },
        // Keep the global error modal's "Continue" a plain dismiss, as the 3DS confirm does.
        { [ERROR_CODES.CARD_EXCEPTION]: () => null },
      );

      if (left()) {
        return;
      }

      const logPayload = { company_id: company.id, order_uuid: orderUuid, status, attempt };

      if (success && response.data.data?.payment_status === ORDER_PAYMENT_STATUSES.PAID) {
        paymentLog.info('spreedly', 'offsite_return_paid', logPayload);

        return onPaymentSuccess(response.data.data);
      }

      if (status === 409) {
        // The bank's or the customer's answer, not ours to alert on.
        paymentLog.info('spreedly', 'offsite_return_not_paid', logPayload);

        return onPaymentError({ cancelled: true, backToCheckout: true });
      }

      if (status === 402) {
        paymentLog.info('spreedly', 'offsite_return_pending', logPayload);

        continue;
      }

      paymentLog.error('spreedly', 'offsite_return_failed', logPayload);

      return;
    }

    // Still not final after the whole budget: the order screen's poll and Pusher (the sweep's
    // order.failed) take it from here. Kept as a trail for ops.
    paymentLog.warn('spreedly', 'offsite_return_budget_spent', { company_id: company.id, order_uuid: orderUuid });
  }

  useEffect(() => {
    let cancelled = false;

    company?.id && handleRedirectResult(() => cancelled);

    return () => {
      cancelled = true;
    };
  }, [company?.id]);
}
